import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import { useModal } from "../../common/hooks/useModal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import StationsService from "../../services/StationsService";

const PAGE_SIZE = 10;

export default function ListAppraiser() {
  const {
    dataModal,
    onCloseModalReset,
    handleChangedModal,
    handleStopSaveModal,
  } = useModal();

  const { t } = useTranslation();
  const [selectedAppraisers, setSelectedAppraisers] = useState<number[]>([]);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);

  const appraisers: any[] = dataModal?.data?.appraisers ?? [];

  const filteredRows = useMemo(() => {
    const lower = filterText.toLowerCase();
    if (!lower) return appraisers;
    return appraisers.filter(
      (a) =>
        String(a.id).includes(lower) ||
        (a.name ?? "").toLowerCase().includes(lower) ||
        (a.email ?? "").toLowerCase().includes(lower) ||
        (a.entidade_nome ?? "").toLowerCase().includes(lower) ||
        (a.role_string ?? "").toLowerCase().includes(lower)
    );
  }, [appraisers, filterText]);

  const pagedRows = useMemo(
    () => filteredRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filteredRows, page]
  );

  const handleToggleRow = (id: number) => {
    setSelectedAppraisers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    const pageIds = pagedRows.map((r) => r.id);
    const allSelected = pageIds.every((id) => selectedAppraisers.includes(id));
    if (allSelected) {
      setSelectedAppraisers((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedAppraisers((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const pageAllSelected =
    pagedRows.length > 0 &&
    pagedRows.every((r) => selectedAppraisers.includes(r.id));

  const pageIndeterminate =
    pagedRows.some((r) => selectedAppraisers.includes(r.id)) && !pageAllSelected;

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (selectedAppraisers.length === 0) {
      toast(t("toast_appraise_empty"), { type: "error" });
      handleStopSaveModal();
      return;
    }

    const selectedAppraisersList = appraisers.filter((appraiser) =>
      selectedAppraisers.includes(appraiser?.id)
    );

    if (selectedAppraisersList.length > 0) {
      dataModal?.data?.setAppraiser(selectedAppraisersList);
      dataModal?.data?.setAppraisers_id(selectedAppraisers.map((a) => a));
    }

    onCloseModalReset();
  };

  useEffect(() => {
    async function fetchInitialAppraisers() {
      if (dataModal?.data?.stationId) {
        try {
          const response = await StationsService.getAppraiserStations(dataModal.data.stationId);
          if (response?.data && Array.isArray(response.data)) {
            setSelectedAppraisers(response.data.map((appraiser: any) => appraiser.id));
          } else if (response?.data?.id) {
            setSelectedAppraisers([response.data.id]);
          }
        } catch {}
      } else if (dataModal?.data?.idAppraiser) {
        setSelectedAppraisers(dataModal.data.idAppraiser);
      }
    }
    fetchInitialAppraisers();

    if (dataModal.loadingBtnSave) {
      handleSubmit();
    }
  }, [dataModal.loadingBtnSave, dataModal.data]);

  return (
    <Box>
      <Box
        sx={{ width: "100%", minWidth: "925px" }}
        component="form"
        onSubmit={handleSubmit}
      >
        <TextField
          size="small"
          placeholder={t("filtrar") || "Filtrar..."}
          value={filterText}
          onChange={(e) => { setFilterText(e.target.value); setPage(0); }}
          sx={{ mb: 1, width: "300px" }}
        />
        <TableContainer component={Paper}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={pageAllSelected}
                    indeterminate={pageIndeterminate}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell>{t("title_header_id") || "ID"}</TableCell>
                <TableCell>{t("title_header_name") || "Nome"}</TableCell>
                <TableCell>{t("title_header_email") || "Email"}</TableCell>
                <TableCell>{t("title_header_entity") || "Entidade"}</TableCell>
                <TableCell>{t("title_header_role") || "Papel"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row) => {
                const isSelected = selectedAppraisers.includes(row.id);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isSelected}
                    onClick={() => handleToggleRow(row.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleToggleRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.entidade_nome}</TableCell>
                    <TableCell>{row.role_string}</TableCell>
                  </TableRow>
                );
              })}
              {pagedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t("no_records") || "Nenhum registro encontrado"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Box>
    </Box>
  );
}
